/**
 * Candidatos internos de la requisición, hasta dos (RH-01).
 *
 * Gente de casa que podría cubrir la vacante. Se eligen de la lista de
 * empleados, pero lo que se guarda es su nombre y su puesto como texto, no una
 * referencia al expediente: el expediente se borra junto con su usuario, así
 * que la referencia fallaría justo cuando la persona se va, que es cuando
 * querrías consultar a quién se había considerado. La lista evita el dedazo;
 * el texto conserva la foto del momento.
 *
 * Marcar al elegido es opcional y se puede hacer después, desde el detalle.
 * Lo que no se puede es marcar a dos: entonces el dato deja de decir nada.
 */

export interface CandidatoInterno {
  name: string
  currentPosition: string
  isSelected?: boolean
  /**
   * Cuándo lo confirmó RH (SRH-313). Solo viene del servidor: no se captura ni se
   * manda al guardar, porque el DTO no lo acepta.
   */
  confirmedAt?: string | null
}

export const MAXIMO_DE_CANDIDATOS_INTERNOS = 2

/** Una fila recién agregada, en blanco y sin marcar. */
export const candidatoEnBlanco = (): CandidatoInterno => ({
  name: '',
  currentPosition: '',
  isSelected: false,
})

/**
 * La lista con la marca puesta en una sola fila.
 *
 * Marcar a alguien desmarca al anterior, en vez de dejar que se acumulen y
 * rechazarlo al guardar: la pantalla no debería permitir armar algo que el
 * servidor va a devolver con un error.
 *
 * Volver a marcar al que ya estaba marcado lo desmarca, que es la forma de
 * decir «me equivoqué, todavía no hay elegido» sin tener que borrar la fila.
 */
export function conElSeleccionado(
  filas: CandidatoInterno[],
  indice: number,
): CandidatoInterno[] {
  const yaEstaba = filas[indice]?.isSelected === true

  return filas.map((fila, i) => ({
    ...fila,
    isSelected: i === indice ? !yaEstaba : false,
  }))
}

/**
 * Las filas que de verdad se mandan.
 *
 * Una fila a medias —con nombre pero sin puesto, o al revés— no se manda: el
 * backend la rechazaría con un 400 que no le dice nada a quien captura, y lo
 * que quiso decir es que ya no la quería. Una fila del todo vacía es
 * exactamente eso.
 */
export function filasQueSeMandan(
  filas: CandidatoInterno[],
): CandidatoInterno[] {
  return filas
    .map((fila) => ({
      ...fila,
      name: fila.name.trim(),
      currentPosition: fila.currentPosition.trim(),
    }))
    .filter((fila) => fila.name !== '' && fila.currentPosition !== '')
}

/**
 * El aviso de la sección, o null si está bien.
 *
 * Solo reclama la fila a medias, porque es la única en la que lo que ve la
 * persona y lo que se va a guardar no coinciden: escribió algo y no se
 * mandaría. La fila vacía se descarta en silencio, que es lo que espera quien
 * agregó un renglón de más.
 */
export function avisoDeCandidatosInternos(
  filas: CandidatoInterno[],
): string | null {
  const aMedias = filas.some((fila) => {
    const nombre = fila.name.trim()
    const puesto = fila.currentPosition.trim()
    return (nombre === '') !== (puesto === '')
  })

  return aMedias
    ? 'Completa el nombre y el puesto actual, o deja el renglón vacío'
    : null
}

/** Lo mínimo que hace falta de un empleado para ofrecerlo en la lista. */
export interface EmpleadoElegible {
  id: number
  nombre: string
  puesto: string
}

/**
 * El nombre y el puesto como los guarda la solicitud.
 *
 * Viene de `/employees/select`, que ya los entrega armados. Antes se leía el
 * expediente completo y había que juntarlos a mano —el nombre del usuario, el
 * puesto de su catálogo—, pero ese endpoint pide el permiso de expedientes y
 * quien levanta la requisición no lo tiene.
 */
export const comoElegible = (empleado: {
  id: number
  fullName?: string | null
  puestoName?: string | null
}): EmpleadoElegible => ({
  id: empleado.id,
  nombre: empleado.fullName?.trim() ?? '',
  puesto: empleado.puestoName?.trim() || '',
})

/**
 * Las opciones del desplegable de una fila.
 *
 * Deja fuera a quien ya está elegido en otra fila —considerar dos veces a la
 * misma persona no dice nada— y mete el nombre que la fila ya traía aunque no
 * esté en la lista: las solicitudes viejas se capturaron a mano, y alguien
 * pudo haber dejado la empresa. Sin esto, abrir una solicitud de antes
 * vaciaría el campo sin avisar.
 */
export function opcionesDeEmpleados(
  empleados: EmpleadoElegible[],
  filas: CandidatoInterno[],
  indice: number,
): string[] {
  const enOtrasFilas = new Set(
    filas
      .filter((_, i) => i !== indice)
      .map((fila) => fila.name.trim())
      .filter((nombre) => nombre !== ''),
  )

  const disponibles = empleados
    .map((empleado) => empleado.nombre)
    .filter((nombre) => nombre !== '' && !enOtrasFilas.has(nombre))

  const yaCapturado = filas[indice]?.name.trim() ?? ''
  const falta = yaCapturado !== '' && !disponibles.includes(yaCapturado)

  const lista = falta ? [yaCapturado, ...disponibles] : disponibles
  return [...new Set(lista)]
}

/** El puesto que le toca a un nombre, para llenarlo solo al elegirlo. */
export const puestoDe = (
  empleados: EmpleadoElegible[],
  nombre: string,
): string => empleados.find((e) => e.nombre === nombre)?.puesto ?? ''
